/**
 * Dış panel (Hayat/Abant) siparişinin kullanıcıya gösterilecek SONUÇ kategorisi.
 *
 * `panelOutcome` üç değer döner: `delivered | cancelled | pending`.  "Ödeme Başarısız"
 * ikisinden AYRI gösterilmeli (kırmızı rozet ama iptal değil).  Ayrıca "Yolda" ≥ 2 sa
 * ise "Uzun süredir yolda" diye ayrı bir kategori var.
 *
 * Bu yardımcı o iki ihtiyacı karşılar — `panelOutcome`'u DEĞİŞTİRMEZ, yalnızca
 * onun üzerine bir kat daha sınıflar.
 *
 * Eşik 2 saat = 7 200 000 ms.  Testler bu sabiti doğrudan kullanır, eşik değişirse
 * ilgili test düşer.
 */
export const ON_ROUTE_LONG_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours

export type ExternalStage =
  'NEW' | 'ON_ROUTE' | 'ON_ROUTE_LONG' | 'DELIVERED' | 'CANCELLED' | 'PAYMENT_FAILED';

/** Boş / tanınamaz girdi. */
export type ExternalStageResult = ExternalStage | null;

/**
 * Girdi: dış panel siparişinin ham verileri.
 * Çıktı: sınıflandırılmış kategori.
 *
 * Mantık:
 *   - Panel kaynağı değil → `null`
 *   - "Teslim Edildi"   → DELIVERED
 *   - "Ödeme Başarısız" → PAYMENT_FAILED   (cancelled değil!)
 *   - "İptal…"          → CANCELLED
 *   - "Yolda" / "Araç Listesinde" / "Araca Gönderiliyor" → ON_ROUTE
 *   - ON_ROUTE + yaş ≥ 2 sa → ON_ROUTE_LONG
 *   - "Yeni…"           → NEW
 *   - Diğer "pending"   → ON_ROUTE
 */
export function externalStage(params: {
  source: string;
  status: string;
  externalStatusRaw: string | null | undefined;
  receivedAt: string;
  now?: number;
}): ExternalStageResult {
  const { source, status, externalStatusRaw, receivedAt, now = Date.now() } = params;

  // Yalnız dış panel kaynakları
  if (source !== 'HAYAT_PANEL' && source !== 'ABANT_PANEL') return null;
  if (status !== 'HANDLED_EXTERNALLY') return null;

  const raw = externalStatusRaw?.trim();
  if (!raw) return null;

  const norm = raw.normalize('NFC').toLocaleUpperCase('tr-TR');

  // delivered
  if (norm.startsWith('TESLİM EDİLDİ')) return 'DELIVERED';

  // Ödeme Başarısız — iptalden AYRI kategori
  if (norm.startsWith('ÖDEME BAŞARISIZ')) return 'PAYMENT_FAILED';

  // Teslim edilemedi önce iptalden yakalanır
  if (norm.startsWith('TESLİM EDİLEMEDİ')) return 'CANCELLED';

  // Diğer iptaller
  if (norm.startsWith('İPTAL')) return 'CANCELLED';

  // Yolda / araç durumları
  const onRoutePatterns = ['YOLDA', 'ARAÇ LİSTESİNDE', 'ARACA GÖNDERİLİYOR'];
  const isOnRoute = onRoutePatterns.some((p) => norm.startsWith(p));

  if (isOnRoute || norm.startsWith('YENİ') || norm.startsWith('YENI')) {
    // Yeni paneli henüz işleme almamış
    if (norm.startsWith('YENİ') || norm.startsWith('YENI')) return 'NEW';

    // Yolda — yaşa bak
    const receivedMs = new Date(receivedAt).getTime();
    if (now - receivedMs >= ON_ROUTE_LONG_THRESHOLD_MS) return 'ON_ROUTE_LONG';
    return 'ON_ROUTE';
  }

  // Tanınmayan ham metin "Yolda" diye etiketlenmez; çağıran ham metni olduğu gibi gösterir.
  return null;
}

/**
 * Siparişler listesinin TEK durum sözlüğü: bizdeki `Order.status` da, kanalın (Hayat/Abant) ham
 * durumu da aynı altı değere düşer. Kanal metni birebir korunur ama yalnız bizim durumdan FARKLIYSA
 * ikinci satırda gri gösterilir.
 */
export type UnifiedStatus =
  'NEW' | 'PREPARING' | 'ON_ROUTE' | 'DELIVERED' | 'CANCELLED' | 'ON_HOLD';

const UNIFIED_COLOR: Record<UnifiedStatus, string> = {
  NEW: 'var(--info)',
  PREPARING: 'var(--primary)',
  ON_ROUTE: 'var(--warning)',
  DELIVERED: 'var(--success)',
  CANCELLED: 'var(--danger)',
  ON_HOLD: 'var(--text-secondary)',
};

export const unifiedStatusColor = (u: UnifiedStatus): string => UNIFIED_COLOR[u];

/** Kanalın ham durum metni → birleşik durum. Tanınmayan / boş / sayısal kod → `null`. */
export function unifiedFromRaw(raw: string | null | undefined): UnifiedStatus | null {
  const norm = raw?.trim().normalize('NFC').toLocaleUpperCase('tr-TR');
  if (!norm) return null;
  if (norm.startsWith('TESLİM EDİLDİ')) return 'DELIVERED';
  if (
    norm.startsWith('TESLİM EDİLEMEDİ') ||
    norm.startsWith('ÖDEME BAŞARISIZ') ||
    norm.startsWith('İPTAL')
  ) {
    return 'CANCELLED';
  }
  if (norm.startsWith('YENİ') || norm.startsWith('YENI')) return 'NEW';
  if (norm.startsWith('YOLDA')) return 'ON_ROUTE';
  if (norm.startsWith('ARAÇ LİSTESİNDE') || norm.startsWith('ARACA GÖNDERİLİYOR')) {
    return 'PREPARING';
  }
  return null;
}

/** Bizim `Order.status` (+ kanal ham durumu) → birleşik durum. */
export function unifiedStatus(
  status: string,
  externalStatusRaw: string | null | undefined,
): UnifiedStatus {
  switch (status) {
    case 'NEW':
      return 'NEW';
    case 'CONFIRMED':
    case 'ASSIGNED':
      return 'PREPARING';
    case 'ON_ROUTE':
      return 'ON_ROUTE';
    case 'DELIVERED':
    case 'LEFT_AT_DOOR':
      return 'DELIVERED';
    case 'FAILED':
    case 'CANCELLED':
      return 'CANCELLED';
    case 'HANDLED_EXTERNALLY':
      return unifiedFromRaw(externalStatusRaw) ?? 'ON_HOLD';
    default:
      return 'ON_HOLD';
  }
}

/**
 * Kanal durumunun ikinci satırı: yalnız panel kaynağında, ham durum tanınıyor ve bizim
 * birleşik durumdan FARKLIYSA `{ raw }` döner; aksi hâlde `null` (tekrar yazılmaz).
 */
export function channelStatusNote(
  source: string,
  status: string,
  externalStatusRaw: string | null | undefined,
): string | null {
  if (source !== 'HAYAT_PANEL' && source !== 'ABANT_PANEL') return null;
  const raw = externalStatusRaw?.trim();
  if (!raw) return null;
  const channel = unifiedFromRaw(raw);
  if (!channel) return null;
  return channel === unifiedStatus(status, externalStatusRaw) ? null : raw;
}
